import React from 'react';
import { FileText, MoreVertical, Pin } from 'lucide-react';
import { formatDateTimeDDMMYYYY } from '../../../../utils/date';
import { LeadNoteHistoryCardBody } from './LeadNoteHistoryCardBody';

const MAX_PINNED_NOTES = 5;

export function LeadNoteHistoryCard({
  title,
  iconVariant = 'note',
  snippet,
  savedAt,
  authorLabel,
  isPinned,
  pinnedCount,
  menuOpen,
  onToggleMenu,
  onTogglePin,
  onDelete,
}: {
  title: string;
  iconVariant?: 'note' | 'pin';
  snippet: string;
  savedAt: string;
  authorLabel?: string | null;
  isPinned: boolean;
  pinnedCount: number;
  menuOpen: boolean;
  onToggleMenu: () => void;
  onTogglePin: () => void;
  onDelete: () => void;
}) {
  const authorPart = authorLabel?.trim() || 'Unbekannt';
  const pinDisabled = !isPinned && pinnedCount >= MAX_PINNED_NOTES;
  const Icon = iconVariant === 'pin' ? Pin : FileText;
  const hasBody = Boolean(snippet.trim());

  return (
    <article className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-app-surface border border-app-border rounded-lg">
      <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
        <Icon
          className={`w-5 h-5 text-amber-600 ${iconVariant === 'pin' ? 'fill-current' : ''}`}
          aria-hidden
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 min-w-0">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-medium text-app-text">{title}</h3>
            <p className="text-xs text-app-text-subtle mt-0.5">
              {formatDateTimeDDMMYYYY(savedAt)} · Erstellt von {authorPart}
            </p>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            <button
              type="button"
              title={
                isPinned
                  ? 'Anpinnung aufheben'
                  : pinDisabled
                    ? `Maximal ${MAX_PINNED_NOTES} Notizen anpinnen`
                    : 'Notiz anpinnen'
              }
              aria-label={isPinned ? 'Anpinnung aufheben' : 'Notiz anpinnen'}
              aria-pressed={isPinned}
              disabled={pinDisabled}
              onClick={onTogglePin}
              className={`p-1.5 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-pipedrive-blue/30 ${
                isPinned
                  ? 'text-amber-600 hover:bg-amber-50'
                  : pinDisabled
                    ? 'text-app-text-subtle/50 cursor-not-allowed'
                    : 'text-app-text-subtle hover:bg-app-hover hover:text-amber-600'
              }`}
            >
              <Pin className={`w-4 h-4 ${isPinned ? 'fill-current' : ''}`} />
            </button>
            <div className="relative shrink-0" data-note-history-menu-root>
              <button
                type="button"
                aria-expanded={menuOpen}
                aria-haspopup="menu"
                aria-label="Notiz-Aktionen"
                onClick={onToggleMenu}
                className="p-1.5 rounded-md text-app-text-subtle hover:bg-app-hover hover:text-app-text focus:outline-none focus-visible:ring-2 focus-visible:ring-pipedrive-blue/30"
              >
                <MoreVertical className="w-4 h-4" />
              </button>
              {menuOpen ? (
                <div
                  className="absolute right-0 top-full mt-1 z-30 min-w-[10rem] rounded-lg border border-app-border bg-app-surface py-1 shadow-lg"
                  role="menu"
                >
                  <button
                    type="button"
                    role="menuitem"
                    className="w-full text-left px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50"
                    onClick={onDelete}
                  >
                    Notiz löschen
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        {hasBody ? (
          <div className="mt-3 overflow-hidden rounded-lg border border-amber-100/90">
            <LeadNoteHistoryCardBody snippet={snippet} />
          </div>
        ) : null}
      </div>
    </article>
  );
}
